import type { Graph, KnowledgeObject } from "@lr-notitia/notitia-designer/schemas";
import { api } from "@lr-notitia/notitia-designer/client";
import { ref } from "vue";
import { defineStore } from "pinia";
import { newGraph, handleGraphModification } from "./graph/utils";

import { datatypes, datatype } from "./graph/datatype";
import { enumItems, enumItem } from "./graph/enumItem";
import { conceptDataobjects, conceptDataobject } from "./graph/c-dataobject";
import { conceptAttributes, conceptAttribute } from "./graph/c-attribute";
import { conceptFragments, conceptFragment } from "./graph/c-fragment";
import { conceptFunctions, conceptFunction } from "./graph/c-function";
import { conceptEmitters, conceptEmitter } from "./graph/c-emitter";
import { conceptEvents, conceptEvent } from "./graph/c-event";
import { conceptTasks, conceptTask } from "./graph/c-task";
import { templateDataobjects, templateDataobject } from "./graph/t-dataobject";
import { templateEvents, templateEvent } from "./graph/t-event";
import { templateTasks, templateTask } from "./graph/t-task";

export const useGraphStore = defineStore("graph", () => {
	const domainId = ref("");
	const graph = ref<Graph>(newGraph());

	function loadGraph(dId: string): Promise<Graph> {
		domainId.value = dId;
		return new Promise((resolve, reject) => {
			api()
				.domain(domainId.value)
				.graphs()
				.get("wip")
				.then((data) => {
					graph.value = data;
					resolve(data);
					api()
						.socket()
						.domain(domainId.value)
						.graph()
						.on((message) => handleGraphModification(message.data));
				})
				.catch(() => reject("Failed to load graph"));
		});
	}
	function patchGraph(patchData: Record<string, KnowledgeObject>) {
		api()
			.domain(domainId.value)
			.graphs()
			.patch(patchData)
			.then((data) => handleGraphModification(data));
	}
	function clearGraph() {
		api().socket().domain(domainId.value).graph().off();
		graph.value = newGraph();
		domainId.value = "";
	}

	return {
		graph,
		loadGraph,
		patchGraph,
		clearGraph,
		descriptor: {
			datatypes: datatypes(graph),
			datatype: datatype(domainId, graph),
			enumItems: enumItems(graph),
			enumItem: enumItem(domainId, graph),
		},
		concept: {
			dataobjects: conceptDataobjects(graph),
			dataobject: conceptDataobject(domainId, graph),
			attributes: conceptAttributes(graph),
			attribute: conceptAttribute(domainId, graph),
			fragments: conceptFragments(graph),
			fragment: conceptFragment(domainId, graph),
			functions: conceptFunctions(graph),
			function: conceptFunction(domainId, graph),
			emitters: conceptEmitters(graph),
			emitter: conceptEmitter(domainId, graph),
			events: conceptEvents(graph),
			event: conceptEvent(domainId, graph),
			tasks: conceptTasks(graph),
			task: conceptTask(domainId, graph),
		},
		template: {
			dataobjects: templateDataobjects(graph),
			dataobject: templateDataobject(domainId, graph),
			events: templateEvents(graph),
			event: templateEvent(domainId, graph),
			tasks: templateTasks(graph),
			task: templateTask(domainId, graph),
		},
	};
});
